import {
  Autocomplete as MuiAutocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
} from "@mui/material";
import React, { useMemo } from "react";
import Label from "../Label/Label";
import ChipsInput from "../../CommunicationHub/ChipInput";

const FormComponent = ({ config, form, otherOptions = [] }) => {
  const {
    id,
    label,
    required = false,
    type = "TextBox",
    props = {},
    containerProps = {},
    options,
  } = config;
  const { values, handleTextBox, handleValue, getValue } = form;
  const value = getValue(id);
  const renderFormComponent = () => {
    switch (type) {
      case "TextBox":
        return (
          <Box {...containerProps}>
            <RequiredLabel label={label} required={required} />
            <TextField
              sx={{
                "& .MuiInputBase-input": {
                  padding: "8px !important",
                },
              }}
              {...props}
              variant="outlined"
              value={value}
              onChange={handleTextBox(id, options)}
            />
          </Box>
        );
      case "Dropdown": {
        const {
          options = [],
          optionKey,
          placeholder,
          isOptionDisabled = () => false,
          ...otherProps
        } = props;
        const allOptions = [...options, ...otherOptions];
        const selectedOption = allOptions.find(
          (option) => option.value === value
        );
        return (
          <Box {...containerProps}>
            <RequiredLabel label={label} required={required} />
            <Select
              sx={{
                "& .MuiSelect-select": {
                  padding: "8px !important",
                },
              }}
              {...otherProps}
              displayEmpty
              renderValue={
                value
                  ? () => selectedOption?.label ?? ""
                  : () => {
                      return (
                        <em
                          style={{
                            color: "rgb(191 191 191)",
                            fontStyle: "normal",
                          }}
                        >
                          {placeholder}
                        </em>
                      );
                    }
              }
              value={value}
              onChange={handleTextBox(id)}
            >
              {[...options, ...otherOptions].map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={isOptionDisabled(option)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      }
      case "Autocomplete": {
        return (
          <Autocomplete
            handleValue={handleValue}
            label={label}
            required={required}
            props={props}
            otherOptions={otherOptions}
            value={value}
            containerProps={containerProps}
            id={id}
          />
        );
      }
      case "Multiselect": {
        return (
          <Multiselect
            handleValue={handleValue}
            label={label}
            required={required}
            props={props}
            otherOptions={otherOptions}
            value={value}
            containerProps={containerProps}
            id={id}
          />
        );
      }
      case "Checkbox": {
        const { options = [], ...otherProps } = props;
        return (
          <Box display={"flex"} flexDirection={"column"} {...containerProps}>
            {options.map((item) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...otherProps}
                    checked={getValue(`${id}.${item.value}`)}
                    onChange={(e) =>
                      handleValue(`${id}.${item.value}`)(e.target.checked)
                    }
                  />
                }
                label={item.label}
              />
            ))}
          </Box>
        );
      }
      case "Radio": {
        const { options = [], ...otherProps } = props;
        return (
          <Box display={"flex"} flexDirection={"column"} {...containerProps}>
            {options.map((item) => (
              <FormControlLabel
                control={
                  <Radio
                    {...otherProps}
                    checked={value === item.value}
                    onChange={(e) => handleValue(id)(item.value)}
                  />
                }
                label={item.label}
              />
            ))}
          </Box>
        );
      }
      case "ChipsInput": {
        return (
          <Box display={"flex"} flexDirection={"column"} {...containerProps}>
            <ChipsInput
              {...props}
              initialValue={value}
              onChange={(value) => handleValue(id)(value)}
            />
          </Box>
        );
      }
    }
  };
  return <>{renderFormComponent()}</>;
};

export default FormComponent;

const Autocomplete = ({
  props,
  label,
  handleValue,
  otherOptions,
  value = "",
  containerProps,
  required,
  id,
}) => {
  const {
    options = [],
    optionKey,
    inputProps,
    placeholder,
    ...otherProps
  } = props;
  const allOptions = useMemo(
    () => [...options, ...otherOptions],
    [options, otherOptions]
  );

  const selectedValue =
    allOptions.find((option) => option.value === value ?? "") ?? "";

  return (
    <Box {...containerProps}>
      <RequiredLabel label={label} required={required} />
      <MuiAutocomplete
        sx={{
          paddingRight: "0 !important",
          marginBottom: "0 !important",
          "& .MuiInputBase-root": {
            padding: "0 !important",
          },
        }}
        {...otherProps}
        renderInput={(params) => (
          <TextField
            sx={{
              "& .MuiInputBase-input": {
                padding: "8px !important",
              },
            }}
            placeholder={placeholder}
            {...params}
            {...inputProps}
            variant="outlined"
          />
        )}
        options={allOptions}
        getOptionKey={(option) => option.value}
        getOptionLabel={(option) => option.label ?? ""}
        value={selectedValue}
        onChange={(e, value) => handleValue(id)(value?.value)}
      />
    </Box>
  );
};
const Multiselect = ({
  props,
  label,
  handleValue,
  otherOptions,
  value = [],
  containerProps,
  required,
  id,
}) => {
  const {
    options = [],
    optionKey,
    inputProps,
    placeholder,
    ...otherProps
  } = props;
  const allOptions = useMemo(
    () => [...options, ...otherOptions],
    [options, otherOptions]
  );

  return (
    <Box {...containerProps}>
      <RequiredLabel label={label} required={required} />
      <MuiAutocomplete
        sx={{
          paddingRight: "0 !important",
          marginBottom: "0 !important",
          "& .MuiInputBase-root": {
            padding: "0 !important",
          },
        }}
        {...otherProps}
        renderInput={(params) => (
          <TextField
            sx={{
              "& .MuiInputBase-input": {
                padding: "8px !important",
              },
            }}
            placeholder={value?.length > 0 ? "" : placeholder}
            {...params}
            {...inputProps}
            variant="outlined"
          />
        )}
        multiple
        options={allOptions}
        getOptionKey={(option) => option.value}
        getOptionLabel={(option) => option.label}
        value={value}
        onChange={(e, value) => handleValue(id)(value)}
      />
    </Box>
  );
};

const RequiredLabel = ({ label, required = false }) => {
  if (label)
    return (
      <Label>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Label>
    );
  return null;
};
